// src/pages/EmployeeDocsApprovalDetail.js
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { auth, db } from "../firebase/firebase";
import {
  doc,
  getDoc,
  onSnapshot,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";

const DOCS = [
  { fileKeys: ["passportFile", "passportFileBack"], expiryKey: "passportExpiry", label: "Passport", pageLabels: ["Page 1", "Page 2"] },
  { fileKeys: ["emiratesIdFile", "emiratesIdFileBack"], expiryKey: "emiratesIdExpiry", label: "Emirates ID", pageLabels: ["Front", "Back"] },
  { fileKeys: ["licenseFile", "licenseFileBack"], expiryKey: "licenseExpiry", label: "Driving License", pageLabels: ["Front", "Back"] },
  { fileKeys: ["rtaPermitFile"], expiryKey: "rtaPermitExpiry", label: "RTA Permit", pageLabels: ["File"] },
  { fileKeys: ["rtaTrainingFile"], expiryKey: "rtaTrainingExpiry", label: "RTA Training", pageLabels: ["File"] },
  { fileKeys: ["healthInsuranceFile"], expiryKey: "healthInsuranceExpiry", label: "Health Insurance", pageLabels: ["File"] },
];

function fmtDate(v) {
  if (!v) return "-";
  if (typeof v === "string") return v.slice(0, 10);
  if (v?.seconds) return new Date(v.seconds * 1000).toISOString().slice(0, 10);
  const d = new Date(v);
  return isNaN(d.getTime()) ? "-" : d.toISOString().slice(0, 10);
}

export default function EmployeeDocsApprovalDetail() {
  const { token, codeHash } = useParams();
  const navigate = useNavigate();

  const role = (localStorage.getItem("role") || "").toLowerCase();
  const canAccess = role === "admin" || role === "md";

  const inviteRef = useMemo(() => (token ? doc(db, "driverDocInvites", token) : null), [token]);
  const subRef = useMemo(() => (token && codeHash ? doc(db, "driverDocSubmissions", token, "submissions", codeHash) : null), [token, codeHash]);

  const [invite, setInvite] = useState(null);
  const [submission, setSubmission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!canAccess) return;
    let unsub = null;

    (async () => {
      setLoading(true);
      try {
        if (inviteRef) {
          const s = await getDoc(inviteRef);
          setInvite(s.exists() ? { id: s.id, ...s.data() } : null);
        }
        if (subRef) {
          unsub = onSnapshot(subRef, (s) => {
            setSubmission(s.exists() ? { id: s.id, ...s.data() } : null);
            setLoading(false);
          });
        } else {
          setLoading(false);
        }
      } catch (e) {
        console.error(e);
        setLoading(false);
      }
    })();

    return () => {
      try {
        unsub?.();
      } catch {}
    };
  }, [canAccess, inviteRef, subRef]);

  const approve = async () => {
    if (!invite?.driverId || !subRef || !submission) {
      setErr("Missing driver or submission.");
      return;
    }

    setBusy(true);
    setMsg("");
    setErr("");

    try {
      // Only update document fields (do NOT touch car assignment / status etc.)
      const updates = {};
      for (const d of DOCS) {
        if (submission[d.expiryKey]) updates[d.expiryKey] = submission[d.expiryKey];
        for (const fileKey of d.fileKeys) {
          if (submission[fileKey]) updates[fileKey] = submission[fileKey];
        }
      }
      updates.updatedAt = serverTimestamp();

      await updateDoc(doc(db, "drivers", invite.driverId), updates);

      await updateDoc(subRef, {
        status: "approved",
        approvedAt: serverTimestamp(),
        approvedBy: auth?.currentUser?.email || auth?.currentUser?.uid || null,
        updatedAt: serverTimestamp(),
      });

      setMsg("✅ Approved and saved to Employee record.");
    } catch (e) {
      console.error(e);
      setErr("Approve failed. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const reject = async () => {
    if (!subRef) return;
    setBusy(true);
    setMsg("");
    setErr("");

    try {
      const c = (comment || "").trim();
      if (!c) {
        setErr("Please write a rejection comment.");
        return;
      }

      await updateDoc(subRef, {
        status: "rejected",
        rejectComment: c,
        rejectedAt: serverTimestamp(),
        rejectedBy: auth?.currentUser?.email || auth?.currentUser?.uid || null,
        updatedAt: serverTimestamp(),
      });

      setMsg("Rejected. The employee will see your comment and can upload again.");
      setComment("");
    } catch (e) {
      console.error(e);
      setErr("Reject failed. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (!canAccess) {
    return (
      <div style={{ padding: 20 }}>
        <h2>Employee Document Review</h2>
        <div style={{ color: "#b91c1c", fontWeight: 700 }}>Not authorized.</div>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ padding: 20 }}>
        <h2>Employee Document Review</h2>
        <div>Loading...</div>
      </div>
    );
  }

  if (!submission) {
    return (
      <div style={{ padding: 20 }}>
        <h2>Employee Document Review</h2>
        <div style={{ color: "#6b7280" }}>Submission not found.</div>
        <button
          onClick={() => navigate(-1)}
          style={{ marginTop: 12, padding: "8px 12px", borderRadius: 8, border: "1px solid #d1d5db" }}
        >
          Back
        </button>
      </div>
    );
  }

  const status = String(submission.status || "draft").toLowerCase();

  return (
    <div style={{ padding: 20 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
        <div>
          <h2 style={{ margin: 0 }}>Employee Document Review</h2>
          <div style={{ color: "#6b7280", fontSize: 13, marginTop: 4 }}>
            Employee: <b>{submission.driverName || submission.driverId || "(unknown)"}</b> — Company: <b>{submission.company || "-"}</b>
          </div>
          <div style={{ color: "#6b7280", fontSize: 13, marginTop: 4 }}>
            Status: <b>{status}</b>
          </div>
        </div>

        <div style={{ display: "flex", gap: 10 }}>
          <button
            onClick={() => navigate(-1)}
            style={{ padding: "9px 12px", borderRadius: 8, border: "1px solid #d1d5db", background: "white", fontWeight: 800 }}
          >
            Back
          </button>
          <button
            onClick={() => navigate("/employee-docs-approvals")}
            style={{ padding: "9px 12px", borderRadius: 8, border: "1px solid #d1d5db", background: "#111827", color: "white", fontWeight: 800 }}
          >
            All submissions
          </button>
        </div>
      </div>

      {msg ? <div style={{ marginTop: 14, color: "#065f46", fontWeight: 700 }}>{msg}</div> : null}
      {err ? <div style={{ marginTop: 14, color: "#b91c1c", fontWeight: 700 }}>{err}</div> : null}

      <div style={{ marginTop: 16, border: "1px solid #e5e7eb", borderRadius: 12, overflow: "hidden", background: "white" }}>
        <div style={{ display: "grid", gridTemplateColumns: "1.2fr 0.9fr 0.9fr", gap: 12, padding: 12, background: "#f9fafb", borderBottom: "1px solid #e5e7eb", fontSize: 12, fontWeight: 900, color: "#374151" }}>
          <div>Document</div>
          <div>Expiry</div>
          <div>File</div>
        </div>

        {DOCS.map((d) => {
          const exp = fmtDate(submission[d.expiryKey]);
          return (
            <div key={d.label} style={{ display: "grid", gridTemplateColumns: "1.2fr 0.9fr 0.9fr", gap: 12, padding: 12, borderBottom: "1px solid #e5e7eb", alignItems: "center" }}>
              <div style={{ fontWeight: 800 }}>{d.label}</div>
              <div style={{ color: "#374151" }}>{exp}</div>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                {d.fileKeys.map((fileKey, index) => {
                  const url = submission[fileKey];
                  return url ? (
                    <a key={fileKey} href={url} target="_blank" rel="noreferrer" style={{ color: "#2563eb", textDecoration: "none", fontWeight: 700 }}>
                      View {d.pageLabels[index]}
                    </a>
                  ) : null;
                })}
                {!d.fileKeys.some((key) => submission[key]) ? <span style={{ color: "#6b7280" }}>-</span> : null}
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ marginTop: 18, padding: 14, borderRadius: 12, border: "1px solid #e5e7eb", background: "white" }}>
        <div style={{ fontWeight: 900, marginBottom: 10 }}>Actions</div>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button
            disabled={busy || status === "approved"}
            onClick={approve}
            style={{ padding: "10px 14px", borderRadius: 10, border: "none", background: status === "approved" ? "#9ca3af" : "#059669", color: "white", fontWeight: 900, cursor: busy || status === "approved" ? "not-allowed" : "pointer" }}
          >
            Approve & Save to Employee
          </button>

          <div style={{ display: "flex", gap: 10, flex: 1, minWidth: 280, alignItems: "center" }}>
            <input
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Reject comment (required)"
              style={{ padding: "10px 12px", borderRadius: 10, border: "1px solid #d1d5db", flex: 1 }}
            />
            <button
              disabled={busy}
              onClick={reject}
              style={{ padding: "10px 14px", borderRadius: 10, border: "none", background: "#b45309", color: "white", fontWeight: 900, cursor: busy ? "not-allowed" : "pointer" }}
            >
              Reject
            </button>
          </div>
        </div>

        <div style={{ marginTop: 10, color: "#6b7280", fontSize: 12 }}>
          Approve action updates ONLY document files and expiry dates on the Employee record. Car assignment, status, salary, etc are untouched.
        </div>
      </div>
    </div>
  );
}
